import { useCallback, useMemo, useRef, useState } from "react";
import { Button, type Column } from "@legal-500/legal500-design-system";
import { ConfirmationModal } from "../../../components/Modal/ConfirmationModal.js";
import { DataTable } from "../../../components/DataTable/DataTable.js";
import {
  APPLY_CHUNK_SIZE,
  type ApplyRowResult,
} from "../../../api/contactRoleEndDateBackfill.service.js";
import {
  parseRollback,
  toRestoreRow,
  type ParseRollbackResult,
  type RollbackRow,
} from "./parseRollback.js";
import { ROLLBACK_HEADERS, downloadRollbackCsv } from "./backfillCsv.js";
import { STATUS_LABELS, countByStatus, runChunkedApply } from "./runApply.js";
import styles from "./ContactRoleEndDateBackfill.module.css";

/**
 * The undo half of the contact-role end-date backfill tool (SOFTSUPP-506):
 * upload or paste a rollback CSV the tool produced earlier, preview exactly
 * what it will restore, and apply it with mode "restore".
 *
 * The parse is strict — any malformed line blocks the whole file rather than
 * being skipped, because a rollback that silently applied only some of its
 * rows would leave the operator believing the run was fully undone. An empty
 * `value` cell is meaningful: restore clears the end date, putting the role
 * back to active.
 */

type Phase = "idle" | "applying" | "done";

type ApplyProgress = {
  done: number;
  total: number;
};

type PreviewRow = {
  id: string;
  contactRoleId: string;
  value: string | null;
};

const PREVIEW_COLUMNS: Column<PreviewRow>[] = [
  {
    header: "Contact role id",
    accessor: "contactRoleId",
    render: (_value, row) => (
      <span className={styles.mono} title={row.contactRoleId}>
        {row.contactRoleId.slice(0, 8)}
      </span>
    ),
  },
  {
    header: "Restore to",
    accessor: "value",
    wrap: true,
    render: (_value, row) =>
      row.value === null ? (
        <span className={`${styles.pill} ${styles.pillWarning}`}>clear the end date</span>
      ) : (
        <span className={styles.mono}>{row.value}</span>
      ),
  },
];

function toPreviewRow(row: RollbackRow): PreviewRow {
  return {
    // Line numbers are unique per parsed row, whatever the file repeats.
    id: `${row.lineNumber}`,
    contactRoleId: row.contactRoleId,
    value: row.value,
  };
}

export function RollbackSection() {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [text, setText] = useState("");
  const [fileName, setFileName] = useState<string | null>(null);
  const [parsed, setParsed] = useState<ParseRollbackResult | null>(null);
  const [phase, setPhase] = useState<Phase>("idle");
  const [isConfirming, setIsConfirming] = useState(false);
  const [progress, setProgress] = useState<ApplyProgress>({ done: 0, total: 0 });
  const [results, setResults] = useState<ApplyRowResult[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  /** Parses whatever is in the box. A fresh preview discards any earlier run. */
  const preview = useCallback((csv: string) => {
    setResults(null);
    setError(null);
    setProgress({ done: 0, total: 0 });
    setPhase("idle");
    setParsed(parseRollback(csv));
  }, []);

  const loadFile = useCallback(
    async (file: File) => {
      let csv: string;
      try {
        csv = await file.text();
      } catch (readError) {
        setError(readError instanceof Error ? readError.message : String(readError));
        return;
      }
      setFileName(file.name);
      setText(csv);
      preview(csv);
    },
    [preview],
  );

  const applyRestore = useCallback(async () => {
    if (!parsed) {
      return;
    }
    setIsConfirming(false);
    setError(null);
    setPhase("applying");

    const { results: runResults, failure } = await runChunkedApply(
      parsed.rows.map(toRestoreRow),
      "restore",
      (done, total) => setProgress({ done, total }),
    );

    setResults(runResults);
    setError(failure);
    setPhase("done");
  }, [parsed]);

  const previewRows = useMemo(() => (parsed ? parsed.rows.map(toPreviewRow) : []), [parsed]);

  const rowCount = parsed?.rows.length ?? 0;
  const canApply = parsed !== null && parsed.errors.length === 0 && rowCount > 0;

  const resultCounts = useMemo(() => countByStatus(results ?? []), [results]);
  const restoredCount = resultCounts("applied");

  const isBusy = phase === "applying";

  return (
    <div className={styles.stack}>
      <div className={styles.card}>
        <div className={styles.cardTitle}>Rollback — restore previous end dates</div>
        <div className={styles.stack}>
          <div className={styles.rowNote}>
            Upload or paste a rollback CSV produced by this tool. Columns:{" "}
            <code>{ROLLBACK_HEADERS.join(",")}</code>. An empty <code>value</code> cell clears the
            end date, putting the role back to active.
          </div>
          <textarea
            className={styles.pasteBox}
            aria-label="Rollback CSV"
            placeholder={`${ROLLBACK_HEADERS.join(",")}\n…`}
            value={text}
            disabled={isBusy}
            onChange={event => {
              setText(event.target.value);
              setFileName(null);
            }}
          />
          <input
            ref={fileInputRef}
            type="file"
            aria-label="Rollback CSV file"
            accept=".csv,.tsv,.txt,text/csv"
            className={styles.hiddenInput}
            disabled={isBusy}
            onChange={event => {
              const chosen = event.target.files?.[0];
              // Clear the input's value, otherwise picking the SAME file again
              // fires no change event and appears to do nothing.
              event.target.value = "";
              if (chosen && !isBusy) {
                void loadFile(chosen);
              }
            }}
          />
          <div className={styles.toolbar}>
            <Button
              variant="outlined"
              disabled={isBusy}
              onClick={() => fileInputRef.current?.click()}
            >
              Browse
            </Button>
            {fileName ? <span className={styles.rowNote}>{fileName}</span> : null}
            <span className={styles.spacer} />
            <Button
              variant="outlined"
              disabled={isBusy || text.trim() === ""}
              onClick={() => preview(text)}
            >
              Preview rollback
            </Button>
          </div>
        </div>
      </div>

      {error ? (
        <div className={`${styles.banner} ${styles.bannerError}`} role="alert">
          {error}
        </div>
      ) : null}

      {parsed && parsed.errors.length > 0 ? (
        <div className={`${styles.banner} ${styles.bannerError}`} role="alert">
          <div>
            {parsed.errors.length} problem{parsed.errors.length === 1 ? "" : "s"} in that file —
            nothing will be restored until they are fixed.
          </div>
          <ul className={styles.errorList}>
            {parsed.errors.map(problem => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {parsed && parsed.errors.length === 0 && phase === "idle" ? (
        <div className={styles.card}>
          <div className={styles.cardTitle}>Rollback preview</div>
          <div className={styles.stack}>
            <DataTable data={previewRows} columns={PREVIEW_COLUMNS} emptyMessage="No rows." />
            <div className={styles.toolbar}>
              <span className={styles.resultCount}>
                {rowCount} row{rowCount === 1 ? "" : "s"} to restore
              </span>
              <span className={styles.spacer} />
              <Button
                variant="contained"
                disabled={!canApply}
                onClick={() => setIsConfirming(true)}
              >
                Apply restore
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {phase === "applying" ? (
        <div className={styles.card}>
          <div className={styles.cardTitle}>Restoring…</div>
          <div className={styles.progress} role="status" aria-live="polite">
            {`${progress.done.toLocaleString()} / ${progress.total.toLocaleString()} rows`}
          </div>
          <div className={styles.rowNote}>
            Sent in batches of {APPLY_CHUNK_SIZE.toLocaleString()}. Keep this tab open.
          </div>
        </div>
      ) : null}

      {phase === "done" && results ? (
        <div className={styles.card}>
          <div className={styles.cardTitle}>Restore result</div>
          <div className={styles.stack}>
            <span className={styles.resultCount}>
              {restoredCount.toLocaleString()} restored
              {(["noop", "skipped_already_ended", "invalid", "error"] as const)
                .filter(status => resultCounts(status) > 0)
                .map(status => ` · ${resultCounts(status)} ${STATUS_LABELS[status].toLowerCase()}`)
                .join("")}
            </span>
            <div className={styles.toolbar}>
              <span className={styles.rowNote}>
                The restore run's own responses carry the end dates it overwrote — keep them too.
              </span>
              <span className={styles.spacer} />
              <Button
                variant="contained"
                disabled={restoredCount === 0}
                onClick={() => downloadRollbackCsv(results, new Date())}
              >
                Download rollback CSV
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {isConfirming && parsed ? (
        <ConfirmationModal
          title="Restore contact role end dates"
          message={`${rowCount} contact role end date${rowCount === 1 ? "" : "s"} will be restored.`}
          warningMessage={
            "Restore writes the CSV's values over whatever the database holds " +
            "now — an empty value cell clears the end date, putting the role " +
            "back to active."
          }
          confirmButtonText={`Restore ${rowCount} value${rowCount === 1 ? "" : "s"}`}
          // A stray click beside the dialog must not stand in for a decision
          // about thousands of contact role rows — Cancel and Escape still do.
          backdropClick={false}
          onClose={() => setIsConfirming(false)}
          onConfirm={applyRestore}
        />
      ) : null}
    </div>
  );
}
